<template>
  <div
    class="filter-pills__container"
    :class="{
      'filter-pills__container--mobile-disabled': !mobileEnabled,
      'filter-pills__container--has-overflow-left': hasOverflowLeft,
      'filter-pills__container--has-overflow-right': hasOverflowRight,
    }"
  >
    <div ref="trackRef" class="filter-pills__track" @scroll.passive="updateOverflow">
      <slot />
    </div>
    <button
      v-if="hasOverflowLeft"
      type="button"
      class="gallery_control gallery_control__previous filter-pills__nav filter-pills__nav--left"
      :aria-label="scrollLeftLabel"
      @click="scrollLeft"
    >
      <span class="arrow" />
    </button>
    <button
      v-if="hasOverflowRight"
      type="button"
      class="gallery_control gallery_control__next filter-pills__nav filter-pills__nav--right"
      :aria-label="scrollRightLabel"
      @click="scrollRight"
    >
      <span class="arrow" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
  mobileEnabled: { type: [Boolean, String], default: true },
  scrollRightLabel: { type: String, default: 'Volgende' },
  scrollLeftLabel: { type: String, default: 'Vorige' },
});

const toBool = (value: boolean | string): boolean => value === true || value === 'true';

const mobileEnabled = computed(() => toBool(props.mobileEnabled));

const trackRef = ref<HTMLElement | null>(null);
const hasOverflowRight = ref(false);
const hasOverflowLeft = ref(false);

let resizeObserver: ResizeObserver | null = null;

const updateOverflow = (): void => {
  const track = trackRef.value;
  if (!track) {
    return;
  }
  const maxScroll = track.scrollWidth - track.clientWidth;
  hasOverflowLeft.value = track.scrollLeft > 1;
  hasOverflowRight.value = track.scrollLeft < maxScroll - 1;
};

const scrollBy = (direction: 1 | -1): void => {
  const track = trackRef.value;
  if (!track) {
    return;
  }
  const delta = Math.max(track.clientWidth * 0.8, 200) * direction;
  track.scrollBy({ left: delta, behavior: 'smooth' });
};

const scrollRight = (): void => scrollBy(1);
const scrollLeft = (): void => scrollBy(-1);

onMounted(() => {
  updateOverflow();
  if (typeof ResizeObserver !== 'undefined' && trackRef.value) {
    resizeObserver = new ResizeObserver(() => updateOverflow());
    resizeObserver.observe(trackRef.value);
  }
  window.addEventListener('resize', updateOverflow);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  window.removeEventListener('resize', updateOverflow);
});
</script>
